/**
 * Central Icons facade for webapp-v2.
 *
 * Importing each glyph from a Central Icons subpath keeps production bundles on
 * per-icon modules while preserving the app's historical icon names.
 */

import type { ComponentType, CSSProperties, SVGProps } from "react";
import type { CentralIconBaseProps } from "central-icons/CentralIconBase";
import { IconAgent } from "central-icons/IconAgent";
import { IconArchive } from "central-icons/IconArchive";
import { IconArrowBottomTop } from "central-icons/IconArrowBottomTop";
import { IconArrowBoxLeft } from "central-icons/IconArrowBoxLeft";
import { IconArrowBoxRight } from "central-icons/IconArrowBoxRight";
import { IconArrowCornerDownLeft } from "central-icons/IconArrowCornerDownLeft";
import { IconArrowDown } from "central-icons/IconArrowDown";
import { IconArrowDownRight } from "central-icons/IconArrowDownRight";
import { IconArrowDownWall } from "central-icons/IconArrowDownWall";
import { IconArrowLeft } from "central-icons/IconArrowLeft";
import { IconArrowLeftRight } from "central-icons/IconArrowLeftRight";
import { IconArrowRedoDown } from "central-icons/IconArrowRedoDown";
import { IconArrowRight } from "central-icons/IconArrowRight";
import { IconArrowRightLeft } from "central-icons/IconArrowRightLeft";
import { IconArrowRotateClockwise } from "central-icons/IconArrowRotateClockwise";
import { IconArrowRotateCounterClockwise } from "central-icons/IconArrowRotateCounterClockwise";
import { IconArrowsRepeat } from "central-icons/IconArrowsRepeat";
import { IconArrowUndoUp } from "central-icons/IconArrowUndoUp";
import { IconArrowUp } from "central-icons/IconArrowUp";
import { IconArrowUpRight } from "central-icons/IconArrowUpRight";
import { IconArrowUpWall } from "central-icons/IconArrowUpWall";
import { IconBarsThree2 } from "central-icons/IconBarsThree2";
import { IconBank } from "central-icons/IconBank";
import { IconBell } from "central-icons/IconBell";
import { IconBookSimple } from "central-icons/IconBookSimple";
import { IconBookmark } from "central-icons/IconBookmark";
import { IconBox2 } from "central-icons/IconBox2";
import { IconBrackets1 } from "central-icons/IconBrackets1";
import { IconBrain1 } from "central-icons/IconBrain1";
import { IconBranch } from "central-icons/IconBranch";
import { IconBubble3 } from "central-icons/IconBubble3";
import { IconBubbleText } from "central-icons/IconBubbleText";
import { IconBubbleWideNotification } from "central-icons/IconBubbleWideNotification";
import { IconBuildings } from "central-icons/IconBuildings";
import { IconCalendarClock } from "central-icons/IconCalendarClock";
import { IconChart3 } from "central-icons/IconChart3";
import { IconChainLink1 } from "central-icons/IconChainLink1";
import { IconCheckCircle2 } from "central-icons/IconCheckCircle2";
import { IconCheckmark2 } from "central-icons/IconCheckmark2";
import { IconCheckmark2Small } from "central-icons/IconCheckmark2Small";
import { IconChevronDownMedium } from "central-icons/IconChevronDownMedium";
import { IconChevronGrabberVertical } from "central-icons/IconChevronGrabberVertical";
import { IconChevronTopMedium } from "central-icons/IconChevronTopMedium";
import { IconChevronLeft } from "central-icons/IconChevronLeft";
import { IconChevronRight } from "central-icons/IconChevronRight";
import { IconCircle } from "central-icons/IconCircle";
import { IconCircleCheck } from "central-icons/IconCircleCheck";
import { IconCircleDashed } from "central-icons/IconCircleDashed";
import { IconCircleInfo } from "central-icons/IconCircleInfo";
import { IconCirclePlaceholderOn } from "central-icons/IconCirclePlaceholderOn";
import { IconCirclePlus } from "central-icons/IconCirclePlus";
import { IconCircleQuestionmark } from "central-icons/IconCircleQuestionmark";
import { IconCircleX } from "central-icons/IconCircleX";
import { IconCreditCard1 } from "central-icons/IconCreditCard1";
import { IconClipboard } from "central-icons/IconClipboard";
import { IconClock } from "central-icons/IconClock";
import { IconClock3OClock } from "central-icons/IconClock3OClock";
import { IconCoins } from "central-icons/IconCoins";
import { IconColorSwatch } from "central-icons/IconColorSwatch";
import { IconCompassRound } from "central-icons/IconCompassRound";
import { IconCompassSquare } from "central-icons/IconCompassSquare";
import { IconComponents } from "central-icons/IconComponents";
import { IconConnectors1 } from "central-icons/IconConnectors1";
import { IconConnectors2 } from "central-icons/IconConnectors2";
import { IconCrossSmall } from "central-icons/IconCrossSmall";
import { IconCurrencyDollar } from "central-icons/IconCurrencyDollar";
import { IconDirection1 } from "central-icons/IconDirection1";
import { IconDotGrid1x3HorizontalTight } from "central-icons/IconDotGrid1x3HorizontalTight";
import { IconDotGrid1x3VerticalTight } from "central-icons/IconDotGrid1x3VerticalTight";
import { IconDollar } from "central-icons/IconDollar";
import { IconDoupleCheck } from "central-icons/IconDoupleCheck";
import { IconEmail1 } from "central-icons/IconEmail1";
import { IconExclamationCircle } from "central-icons/IconExclamationCircle";
import { IconExclamationTriangle } from "central-icons/IconExclamationTriangle";
import { IconEyeOpen } from "central-icons/IconEyeOpen";
import { IconEyeSlash } from "central-icons/IconEyeSlash";
import { IconFileDownload } from "central-icons/IconFileDownload";
import { IconFileText } from "central-icons/IconFileText";
import { IconFilter2 } from "central-icons/IconFilter2";
import { IconFire1 } from "central-icons/IconFire1";
import { IconFloppyDisk1 } from "central-icons/IconFloppyDisk1";
import { IconGauge } from "central-icons/IconGauge";
import { IconGift1 } from "central-icons/IconGift1";
import { IconGlobe2 } from "central-icons/IconGlobe2";
import { IconGold } from "central-icons/IconGold";
import { IconGraduateCap } from "central-icons/IconGraduateCap";
import { IconHammer } from "central-icons/IconHammer";
import { IconInboxEmpty } from "central-icons/IconInboxEmpty";
import { IconInvite } from "central-icons/IconInvite";
import { IconKey2 } from "central-icons/IconKey2";
import { IconLaw } from "central-icons/IconLaw";
import { IconLayersThree } from "central-icons/IconLayersThree";
import { IconLayersTwo } from "central-icons/IconLayersTwo";
import { IconLayoutDashboard } from "central-icons/IconLayoutDashboard";
import { IconLayoutTop } from "central-icons/IconLayoutTop";
import { IconLineChart1 } from "central-icons/IconLineChart1";
import { IconLiveActivity } from "central-icons/IconLiveActivity";
import { IconLoader } from "central-icons/IconLoader";
import { IconLock } from "central-icons/IconLock";
import { IconMagicWand } from "central-icons/IconMagicWand";
import { IconMagnifyingGlass2 } from "central-icons/IconMagnifyingGlass2";
import { IconMapPin } from "central-icons/IconMapPin";
import { IconMathBasic } from "central-icons/IconMathBasic";
import { IconMinusSmall } from "central-icons/IconMinusSmall";
import { IconMoneyHand } from "central-icons/IconMoneyHand";
import { IconMoon } from "central-icons/IconMoon";
import { IconPaperPlaneTopRight } from "central-icons/IconPaperPlaneTopRight";
import { IconPause } from "central-icons/IconPause";
import { IconPeople } from "central-icons/IconPeople";
import { IconPlay } from "central-icons/IconPlay";
import { IconPlusSmall } from "central-icons/IconPlusSmall";
import { IconQrCode } from "central-icons/IconQrCode";
import { IconSignature } from "central-icons/IconSignature";
import { IconRadar } from "central-icons/IconRadar";
import { IconReceiptBill } from "central-icons/IconReceiptBill";
import { IconRocket } from "central-icons/IconRocket";
import { IconRotate360Left } from "central-icons/IconRotate360Left";
import { IconRotate360Right } from "central-icons/IconRotate360Right";
import { IconSettingsGear2 } from "central-icons/IconSettingsGear2";
import { IconSettingsSliderHor } from "central-icons/IconSettingsSliderHor";
import { IconShareOs } from "central-icons/IconShareOs";
import { IconShieldCheck } from "central-icons/IconShieldCheck";
import { IconShieldCrossed } from "central-icons/IconShieldCrossed";
import { IconSidebarSimpleLeftWide } from "central-icons/IconSidebarSimpleLeftWide";
import { IconSortArrowUpDown } from "central-icons/IconSortArrowUpDown";
import { IconSparklesThree } from "central-icons/IconSparklesThree";
import { IconSquareArrowTopRight2 } from "central-icons/IconSquareArrowTopRight2";
import { IconSquareBehindSquare1 } from "central-icons/IconSquareBehindSquare1";
import { IconSquareChecklist } from "central-icons/IconSquareChecklist";
import { IconStar } from "central-icons/IconStar";
import { IconStepForwards } from "central-icons/IconStepForwards";
import { IconSticker } from "central-icons/IconSticker";
import { IconStorage } from "central-icons/IconStorage";
import { IconSuitcaseWork } from "central-icons/IconSuitcaseWork";
import { IconSun } from "central-icons/IconSun";
import { IconTarget } from "central-icons/IconTarget";
import { IconTelescope } from "central-icons/IconTelescope";
import { IconTicket } from "central-icons/IconTicket";
import { IconTodos } from "central-icons/IconTodos";
import { IconTradingViewCandles } from "central-icons/IconTradingViewCandles";
import { IconTrashRounded } from "central-icons/IconTrashRounded";
import { IconTrending1 } from "central-icons/IconTrending1";
import { IconTrending2 } from "central-icons/IconTrending2";
import { IconUnarchiv } from "central-icons/IconUnarchiv";
import { IconUnlocked } from "central-icons/IconUnlocked";
import { IconUser } from "central-icons/IconUser";
import { IconWallet1 } from "central-icons/IconWallet1";
import { IconWallet2 } from "central-icons/IconWallet2";
import { IconWallet5 } from "central-icons/IconWallet5";
import { IconWreathSimple } from "central-icons/IconWreathSimple";
import { IconZap } from "central-icons/IconZap";

type CentralIconStyle = CSSProperties & {
  readonly "--central-icon-stroke-width"?: string;
};

export type CentralIconComponentProps = Omit<
  CentralIconBaseProps,
  "ariaHidden"
> & {
  readonly ariaHidden?: boolean;
  readonly strokeWidth?: number | string;
};

export type CentralIconComponent = ComponentType<CentralIconComponentProps>;

const CENTRAL_ICON_STROKE_OVERRIDE_CLASS_NAME = "central-icon-stroke-override";

function mergeClassNames(
  ...classNames: ReadonlyArray<string | undefined>
): string | undefined {
  const merged = classNames.filter(Boolean).join(" ");
  return merged.length > 0 ? merged : undefined;
}

function withStrokeWidthStyle(
  style: CSSProperties | undefined,
  strokeWidth: number | string | undefined,
): CentralIconStyle | undefined {
  if (strokeWidth === undefined) {
    return style;
  }

  return {
    ...style,
    "--central-icon-stroke-width": String(strokeWidth),
  };
}

function normalizeAriaHidden(
  value: SVGProps<SVGSVGElement>["aria-hidden"],
): boolean | undefined {
  if (value === undefined) {
    return undefined;
  }

  return value === true || value === "true";
}

function resolveAriaHidden(
  ariaHidden: boolean | undefined,
  ariaHiddenAttr: SVGProps<SVGSVGElement>["aria-hidden"],
): boolean | undefined {
  return ariaHidden ?? normalizeAriaHidden(ariaHiddenAttr);
}

function createCentralIcon(
  IconComponent: ComponentType<CentralIconBaseProps>,
  displayName: string,
): CentralIconComponent {
  const WrappedCentralIcon: CentralIconComponent = ({
    ariaHidden,
    className,
    strokeWidth,
    style,
    ["aria-hidden"]: ariaHiddenAttr,
    ...rest
  }) => (
    <IconComponent
      {...rest}
      ariaHidden={resolveAriaHidden(ariaHidden, ariaHiddenAttr)}
      className={mergeClassNames(
        strokeWidth === undefined
          ? undefined
          : CENTRAL_ICON_STROKE_OVERRIDE_CLASS_NAME,
        className,
      )}
      style={withStrokeWidthStyle(style, strokeWidth)}
    />
  );

  WrappedCentralIcon.displayName = displayName;
  return WrappedCentralIcon;
}

export const ActivitySparkIcon = /* @__PURE__ */ createCentralIcon(
  IconLiveActivity,
  "ActivitySparkIcon",
);
export const Add01Icon = /* @__PURE__ */ createCentralIcon(
  IconPlusSmall,
  "Add01Icon",
);
export const AddCircleIcon = /* @__PURE__ */ createCentralIcon(
  IconCirclePlus,
  "AddCircleIcon",
);
export const AffiliateIcon = /* @__PURE__ */ createCentralIcon(
  IconInvite,
  "AffiliateIcon",
);
export const AiBrain01Icon = /* @__PURE__ */ createCentralIcon(
  IconBrain1,
  "AiBrain01Icon",
);
export const Alert02Icon = /* @__PURE__ */ createCentralIcon(
  IconExclamationTriangle,
  "Alert02Icon",
);
export const AlertCircleIcon = /* @__PURE__ */ createCentralIcon(
  IconExclamationCircle,
  "AlertCircleIcon",
);
export const ArrowDown01Icon = /* @__PURE__ */ createCentralIcon(
  IconArrowDown,
  "ArrowDown01Icon",
);
export const ArrowDownRight01Icon = /* @__PURE__ */ createCentralIcon(
  IconArrowDownRight,
  "ArrowDownRight01Icon",
);
export const ArrowLeft01Icon = /* @__PURE__ */ createCentralIcon(
  IconArrowLeft,
  "ArrowLeft01Icon",
);
export const ArrowLeftRightIcon = /* @__PURE__ */ createCentralIcon(
  IconArrowLeftRight,
  "ArrowLeftRightIcon",
);
export const ArrowRight01Icon = /* @__PURE__ */ createCentralIcon(
  IconArrowRight,
  "ArrowRight01Icon",
);
export const ArrowUp01Icon = /* @__PURE__ */ createCentralIcon(
  IconArrowUp,
  "ArrowUp01Icon",
);
export const ArrowUpDownIcon = /* @__PURE__ */ createCentralIcon(
  IconArrowBottomTop,
  "ArrowUpDownIcon",
);
export const ArrowUpRight01Icon = /* @__PURE__ */ createCentralIcon(
  IconArrowUpRight,
  "ArrowUpRight01Icon",
);
export const Award01Icon = /* @__PURE__ */ createCentralIcon(
  IconWreathSimple,
  "Award01Icon",
);
export const BriefcaseIcon = /* @__PURE__ */ createCentralIcon(
  IconSuitcaseWork,
  "BriefcaseIcon",
);
export const Building02Icon = /* @__PURE__ */ createCentralIcon(
  IconBuildings,
  "Building02Icon",
);
export const Cancel01Icon = /* @__PURE__ */ createCentralIcon(
  IconCrossSmall,
  "Cancel01Icon",
);
export const CancelCircleIcon = /* @__PURE__ */ createCentralIcon(
  IconCircleX,
  "CancelCircleIcon",
);
export const ChartCandlestickIcon = /* @__PURE__ */ createCentralIcon(
  IconTradingViewCandles,
  "ChartCandlestickIcon",
);
export const Chatting01Icon = /* @__PURE__ */ createCentralIcon(
  IconBubble3,
  "Chatting01Icon",
);
export const CheckmarkCircle01Icon = /* @__PURE__ */ createCentralIcon(
  IconCircleCheck,
  "CheckmarkCircle01Icon",
);
export const CheckmarkCircle02Icon = /* @__PURE__ */ createCentralIcon(
  IconCircleCheck,
  "CheckmarkCircle02Icon",
);
export const CircleIcon = /* @__PURE__ */ createCentralIcon(
  IconCircle,
  "CircleIcon",
);
export const Clock03Icon = /* @__PURE__ */ createCentralIcon(
  IconClock,
  "Clock03Icon",
);
export const Coins01Icon = /* @__PURE__ */ createCentralIcon(
  IconCoins,
  "Coins01Icon",
);
export const CoinsBitcoinIcon = /* @__PURE__ */ createCentralIcon(
  IconCoins,
  "CoinsBitcoinIcon",
);
export const Compass01Icon = /* @__PURE__ */ createCentralIcon(
  IconCompassRound,
  "Compass01Icon",
);
export const ComponentIcon = /* @__PURE__ */ createCentralIcon(
  IconComponents,
  "ComponentIcon",
);
export const DashboardSquare02Icon = /* @__PURE__ */ createCentralIcon(
  IconLayoutDashboard,
  "DashboardSquare02Icon",
);
export const Delete02Icon = /* @__PURE__ */ createCentralIcon(
  IconTrashRounded,
  "Delete02Icon",
);
export const DiscoverCircleIcon = /* @__PURE__ */ createCentralIcon(
  IconCompassSquare,
  "DiscoverCircleIcon",
);
export const DragDropVerticalIcon = /* @__PURE__ */ createCentralIcon(
  IconChevronGrabberVertical,
  "DragDropVerticalIcon",
);
export const FilterIcon = /* @__PURE__ */ createCentralIcon(
  IconFilter2,
  "FilterIcon",
);
export const FireIcon = /* @__PURE__ */ createCentralIcon(
  IconFire1,
  "FireIcon",
);
export const GitBranchIcon = /* @__PURE__ */ createCentralIcon(
  IconBranch,
  "GitBranchIcon",
);
export const BankIcon = /* @__PURE__ */ createCentralIcon(IconBank, "BankIcon");
export const GlobeIcon = /* @__PURE__ */ createCentralIcon(
  IconGlobe2,
  "GlobeIcon",
);
export const GoldIcon = /* @__PURE__ */ createCentralIcon(IconGold, "GoldIcon");
export const SignatureIcon = /* @__PURE__ */ createCentralIcon(
  IconSignature,
  "SignatureIcon",
);
export const HelpCircleIcon = /* @__PURE__ */ createCentralIcon(
  IconCircleQuestionmark,
  "HelpCircleIcon",
);
export const InboxIcon = /* @__PURE__ */ createCentralIcon(
  IconInboxEmpty,
  "InboxIcon",
);
export const InformationCircleIcon = /* @__PURE__ */ createCentralIcon(
  IconCircleInfo,
  "InformationCircleIcon",
);
export const JusticeScale01Icon = /* @__PURE__ */ createCentralIcon(
  IconLaw,
  "JusticeScale01Icon",
);
export const Key01Icon = /* @__PURE__ */ createCentralIcon(
  IconKey2,
  "Key01Icon",
);
export const Layers01Icon = /* @__PURE__ */ createCentralIcon(
  IconLayersTwo,
  "Layers01Icon",
);
export const LayoutTopIcon = /* @__PURE__ */ createCentralIcon(
  IconLayoutTop,
  "LayoutTopIcon",
);
export const Link01Icon = /* @__PURE__ */ createCentralIcon(
  IconChainLink1,
  "Link01Icon",
);
export const Loading03Icon = /* @__PURE__ */ createCentralIcon(
  IconLoader,
  "Loading03Icon",
);
export const LockKeyIcon = /* @__PURE__ */ createCentralIcon(
  IconLock,
  "LockKeyIcon",
);
export const Login01Icon = /* @__PURE__ */ createCentralIcon(
  IconArrowBoxRight,
  "Login01Icon",
);
export const Logout01Icon = /* @__PURE__ */ createCentralIcon(
  IconArrowBoxLeft,
  "Logout01Icon",
);
export const MagicWand01Icon = /* @__PURE__ */ createCentralIcon(
  IconMagicWand,
  "MagicWand01Icon",
);
export const Mail01Icon = /* @__PURE__ */ createCentralIcon(
  IconEmail1,
  "Mail01Icon",
);
export const MapPin01Icon = /* @__PURE__ */ createCentralIcon(
  IconMapPin,
  "MapPin01Icon",
);
export const Menu01Icon = /* @__PURE__ */ createCentralIcon(
  IconBarsThree2,
  "Menu01Icon",
);
export const Message02Icon = /* @__PURE__ */ createCentralIcon(
  IconBubble3,
  "Message02Icon",
);
export const MinusSignIcon = /* @__PURE__ */ createCentralIcon(
  IconMinusSmall,
  "MinusSignIcon",
);
export const EyeOpenIcon = /* @__PURE__ */ createCentralIcon(
  IconEyeOpen,
  "EyeOpenIcon",
);
export const EyeSlashIcon = /* @__PURE__ */ createCentralIcon(
  IconEyeSlash,
  "EyeSlashIcon",
);
export const Moon02Icon = /* @__PURE__ */ createCentralIcon(
  IconMoon,
  "Moon02Icon",
);
export const MoreVerticalIcon = /* @__PURE__ */ createCentralIcon(
  IconDotGrid1x3VerticalTight,
  "MoreVerticalIcon",
);
export const Notification02Icon = /* @__PURE__ */ createCentralIcon(
  IconBell,
  "Notification02Icon",
);
export const NotificationBubbleIcon = /* @__PURE__ */ createCentralIcon(
  IconBubbleWideNotification,
  "NotificationBubbleIcon",
);
export const OctagonXIcon = /* @__PURE__ */ createCentralIcon(
  IconCircleX,
  "OctagonXIcon",
);
export const Orbit01Icon = /* @__PURE__ */ createCentralIcon(
  IconRadar,
  "Orbit01Icon",
);
export const PanelLeftIcon = /* @__PURE__ */ createCentralIcon(
  IconSidebarSimpleLeftWide,
  "PanelLeftIcon",
);
export const PlusSignCircleIcon = /* @__PURE__ */ createCentralIcon(
  IconCirclePlus,
  "PlusSignCircleIcon",
);
export const PlusSignIcon = /* @__PURE__ */ createCentralIcon(
  IconPlusSmall,
  "PlusSignIcon",
);
export const PromotionIcon = /* @__PURE__ */ createCentralIcon(
  IconSticker,
  "PromotionIcon",
);
export const Radar02Icon = /* @__PURE__ */ createCentralIcon(
  IconRadar,
  "Radar02Icon",
);
export const Redo02Icon = /* @__PURE__ */ createCentralIcon(
  IconArrowRedoDown,
  "Redo02Icon",
);
export const Refresh01Icon = /* @__PURE__ */ createCentralIcon(
  IconArrowsRepeat,
  "Refresh01Icon",
);
export const RefreshIcon = /* @__PURE__ */ createCentralIcon(
  IconArrowsRepeat,
  "RefreshIcon",
);
export const Remove01Icon = /* @__PURE__ */ createCentralIcon(
  IconMinusSmall,
  "Remove01Icon",
);
export const Route02Icon = /* @__PURE__ */ createCentralIcon(
  IconDirection1,
  "Route02Icon",
);
export const SaveIcon = /* @__PURE__ */ createCentralIcon(
  IconFloppyDisk1,
  "SaveIcon",
);
export const Search01Icon = /* @__PURE__ */ createCentralIcon(
  IconMagnifyingGlass2,
  "Search01Icon",
);
export const SearchIcon = /* @__PURE__ */ createCentralIcon(
  IconMagnifyingGlass2,
  "SearchIcon",
);
export const SecurityCheckIcon = /* @__PURE__ */ createCentralIcon(
  IconShieldCheck,
  "SecurityCheckIcon",
);
export const SecurityWarningIcon = /* @__PURE__ */ createCentralIcon(
  IconShieldCrossed,
  "SecurityWarningIcon",
);
export const Settings02Icon = /* @__PURE__ */ createCentralIcon(
  IconSettingsGear2,
  "Settings02Icon",
);
export const Sorting05Icon = /* @__PURE__ */ createCentralIcon(
  IconSortArrowUpDown,
  "Sorting05Icon",
);
export const SparklesIcon = /* @__PURE__ */ createCentralIcon(
  IconSparklesThree,
  "SparklesIcon",
);
export const SquareArrowUpRightIcon = /* @__PURE__ */ createCentralIcon(
  IconSquareArrowTopRight2,
  "SquareArrowUpRightIcon",
);
export const StarIcon = /* @__PURE__ */ createCentralIcon(IconStar, "StarIcon");
export const Summation01Icon = /* @__PURE__ */ createCentralIcon(
  IconMathBasic,
  "Summation01Icon",
);
export const Sun02Icon = /* @__PURE__ */ createCentralIcon(
  IconSun,
  "Sun02Icon",
);
export const SwatchIcon = /* @__PURE__ */ createCentralIcon(
  IconColorSwatch,
  "SwatchIcon",
);
export const Telescope01Icon = /* @__PURE__ */ createCentralIcon(
  IconTelescope,
  "Telescope01Icon",
);
export const Tick01Icon = /* @__PURE__ */ createCentralIcon(
  IconCheckmark2Small,
  "Tick01Icon",
);
export const Tick02Icon = /* @__PURE__ */ createCentralIcon(
  IconCheckmark2,
  "Tick02Icon",
);
export const Ticket01Icon = /* @__PURE__ */ createCentralIcon(
  IconTicket,
  "Ticket01Icon",
);
export const Undo02Icon = /* @__PURE__ */ createCentralIcon(
  IconArrowUndoUp,
  "Undo02Icon",
);
export const UnfoldMoreIcon = /* @__PURE__ */ createCentralIcon(
  IconArrowBottomTop,
  "UnfoldMoreIcon",
);
export const UserCircle02Icon = /* @__PURE__ */ createCentralIcon(
  IconUser,
  "UserCircle02Icon",
);
export const UserCircleIcon = /* @__PURE__ */ createCentralIcon(
  IconUser,
  "UserCircleIcon",
);
export const Wallet01Icon = /* @__PURE__ */ createCentralIcon(
  IconWallet1,
  "Wallet01Icon",
);
export const Wallet02Icon = /* @__PURE__ */ createCentralIcon(
  IconWallet2,
  "Wallet02Icon",
);
export const WorkflowSquare01Icon = /* @__PURE__ */ createCentralIcon(
  IconConnectors1,
  "WorkflowSquare01Icon",
);
export const WorkflowSquare03Icon = /* @__PURE__ */ createCentralIcon(
  IconConnectors2,
  "WorkflowSquare03Icon",
);
export const ZapIcon = /* @__PURE__ */ createCentralIcon(IconZap, "ZapIcon");

// Compatibility aliases for the former external-icon tail.
export const Activity = /* @__PURE__ */ createCentralIcon(
  IconLiveActivity,
  "Activity",
);
export const AlertCircle = /* @__PURE__ */ createCentralIcon(
  IconExclamationCircle,
  "AlertCircle",
);
export const ArrowDown = /* @__PURE__ */ createCentralIcon(
  IconArrowDown,
  "ArrowDown",
);
export const ArrowDownToLine = /* @__PURE__ */ createCentralIcon(
  IconArrowDownWall,
  "ArrowDownToLine",
);
export const ArrowRight = /* @__PURE__ */ createCentralIcon(
  IconArrowRight,
  "ArrowRight",
);
export const ArrowRightLeft = /* @__PURE__ */ createCentralIcon(
  IconArrowRightLeft,
  "ArrowRightLeft",
);
export const ArrowUp = /* @__PURE__ */ createCentralIcon(
  IconArrowUp,
  "ArrowUp",
);
export const ArrowUpDown = /* @__PURE__ */ createCentralIcon(
  IconArrowBottomTop,
  "ArrowUpDown",
);
export const ArrowUpFromLine = /* @__PURE__ */ createCentralIcon(
  IconArrowUpWall,
  "ArrowUpFromLine",
);
export const ArrowUpRight = /* @__PURE__ */ createCentralIcon(
  IconArrowUpRight,
  "ArrowUpRight",
);
export const Archive = /* @__PURE__ */ createCentralIcon(
  IconArchive,
  "Archive",
);
export const ArchiveRestore = /* @__PURE__ */ createCentralIcon(
  IconUnarchiv,
  "ArchiveRestore",
);
export const BadgeDollarSign = /* @__PURE__ */ createCentralIcon(
  IconCurrencyDollar,
  "BadgeDollarSign",
);
export const BarChart3 = /* @__PURE__ */ createCentralIcon(
  IconChart3,
  "BarChart3",
);
export const Bell = /* @__PURE__ */ createCentralIcon(IconBell, "Bell");
export const Bookmark = /* @__PURE__ */ createCentralIcon(
  IconBookmark,
  "Bookmark",
);
export const BookOpen = /* @__PURE__ */ createCentralIcon(
  IconBookSimple,
  "BookOpen",
);
export const Bot = /* @__PURE__ */ createCentralIcon(IconAgent, "Bot");
export const Box = /* @__PURE__ */ createCentralIcon(IconBox2, "Box");
export const Braces = /* @__PURE__ */ createCentralIcon(
  IconBrackets1,
  "Braces",
);
export const Brain = /* @__PURE__ */ createCentralIcon(IconBrain1, "Brain");
export const Building2 = /* @__PURE__ */ createCentralIcon(
  IconBuildings,
  "Building2",
);
export const CalendarClock = /* @__PURE__ */ createCentralIcon(
  IconCalendarClock,
  "CalendarClock",
);
export const Check = /* @__PURE__ */ createCentralIcon(IconCheckmark2, "Check");
export const CheckCheck = /* @__PURE__ */ createCentralIcon(
  IconDoupleCheck,
  "CheckCheck",
);
export const CheckCircle2 = /* @__PURE__ */ createCentralIcon(
  IconCheckCircle2,
  "CheckCircle2",
);
export const ChevronDown = /* @__PURE__ */ createCentralIcon(
  IconChevronDownMedium,
  "ChevronDown",
);
export const ChevronLeft = /* @__PURE__ */ createCentralIcon(
  IconChevronLeft,
  "ChevronLeft",
);
export const ChevronRight = /* @__PURE__ */ createCentralIcon(
  IconChevronRight,
  "ChevronRight",
);
export const ChevronUp = /* @__PURE__ */ createCentralIcon(
  IconChevronTopMedium,
  "ChevronUp",
);
export const ChevronsUpDown = /* @__PURE__ */ createCentralIcon(
  IconChevronGrabberVertical,
  "ChevronsUpDown",
);
export const Circle = /* @__PURE__ */ createCentralIcon(
  IconCirclePlaceholderOn,
  "Circle",
);
export const CircleAlert = /* @__PURE__ */ createCentralIcon(
  IconExclamationCircle,
  "CircleAlert",
);
export const CircleDashed = /* @__PURE__ */ createCentralIcon(
  IconCircleDashed,
  "CircleDashed",
);
export const CircleDollarSign = /* @__PURE__ */ createCentralIcon(
  IconDollar,
  "CircleDollarSign",
);
export const CircleHelp = /* @__PURE__ */ createCentralIcon(
  IconCircleQuestionmark,
  "CircleHelp",
);
export const Clipboard = /* @__PURE__ */ createCentralIcon(
  IconClipboard,
  "Clipboard",
);
export const Clock = /* @__PURE__ */ createCentralIcon(IconClock, "Clock");
export const Clock3 = /* @__PURE__ */ createCentralIcon(
  IconClock3OClock,
  "Clock3",
);
export const Coins = /* @__PURE__ */ createCentralIcon(IconCoins, "Coins");
export const Compass = /* @__PURE__ */ createCentralIcon(
  IconCompassRound,
  "Compass",
);
export const Copy = /* @__PURE__ */ createCentralIcon(
  IconSquareBehindSquare1,
  "Copy",
);
export const CornerDownLeft = /* @__PURE__ */ createCentralIcon(
  IconArrowCornerDownLeft,
  "CornerDownLeft",
);
export const CreditCard = /* @__PURE__ */ createCentralIcon(
  IconCreditCard1,
  "CreditCard",
);
export const Database = /* @__PURE__ */ createCentralIcon(
  IconStorage,
  "Database",
);
export const Download = /* @__PURE__ */ createCentralIcon(
  IconFileDownload,
  "Download",
);
export const ExternalLink = /* @__PURE__ */ createCentralIcon(
  IconSquareArrowTopRight2,
  "ExternalLink",
);
export const FileText = /* @__PURE__ */ createCentralIcon(
  IconFileText,
  "FileText",
);
export const Gauge = /* @__PURE__ */ createCentralIcon(IconGauge, "Gauge");
export const Gift = /* @__PURE__ */ createCentralIcon(IconGift1, "Gift");
export const GitBranch = /* @__PURE__ */ createCentralIcon(
  IconBranch,
  "GitBranch",
);
export const Globe = /* @__PURE__ */ createCentralIcon(IconGlobe2, "Globe");
export const GraduationCap = /* @__PURE__ */ createCentralIcon(
  IconGraduateCap,
  "GraduationCap",
);
export const HandCoins = /* @__PURE__ */ createCentralIcon(
  IconMoneyHand,
  "HandCoins",
);
export const Layers3 = /* @__PURE__ */ createCentralIcon(
  IconLayersThree,
  "Layers3",
);
export const LineChart = /* @__PURE__ */ createCentralIcon(
  IconLineChart1,
  "LineChart",
);
export const ListChecks = /* @__PURE__ */ createCentralIcon(
  IconSquareChecklist,
  "ListChecks",
);
export const ListTodo = /* @__PURE__ */ createCentralIcon(
  IconTodos,
  "ListTodo",
);
export const Loader2 = /* @__PURE__ */ createCentralIcon(IconLoader, "Loader2");
export const LoaderCircle = /* @__PURE__ */ createCentralIcon(
  IconLoader,
  "LoaderCircle",
);
export const Lock = /* @__PURE__ */ createCentralIcon(IconLock, "Lock");
export const MessageSquareText = /* @__PURE__ */ createCentralIcon(
  IconBubbleText,
  "MessageSquareText",
);
export const Minus = /* @__PURE__ */ createCentralIcon(IconMinusSmall, "Minus");
export const MoreHorizontal = /* @__PURE__ */ createCentralIcon(
  IconDotGrid1x3HorizontalTight,
  "MoreHorizontal",
);
export const Pause = /* @__PURE__ */ createCentralIcon(IconPause, "Pause");
export const Play = /* @__PURE__ */ createCentralIcon(IconPlay, "Play");
export const Plus = /* @__PURE__ */ createCentralIcon(IconPlusSmall, "Plus");
export const QrCode = /* @__PURE__ */ createCentralIcon(IconQrCode, "QrCode");
export const ReceiptText = /* @__PURE__ */ createCentralIcon(
  IconReceiptBill,
  "ReceiptText",
);
export const RefreshCcw = /* @__PURE__ */ createCentralIcon(
  IconArrowRotateCounterClockwise,
  "RefreshCcw",
);
export const RefreshCw = /* @__PURE__ */ createCentralIcon(
  IconArrowRotateClockwise,
  "RefreshCw",
);
export const Rocket = /* @__PURE__ */ createCentralIcon(IconRocket, "Rocket");
export const RotateCcw = /* @__PURE__ */ createCentralIcon(
  IconRotate360Left,
  "RotateCcw",
);
export const RotateCw = /* @__PURE__ */ createCentralIcon(
  IconRotate360Right,
  "RotateCw",
);
export const Route = /* @__PURE__ */ createCentralIcon(IconDirection1, "Route");
export const Scale = /* @__PURE__ */ createCentralIcon(IconLaw, "Scale");
export const Search = /* @__PURE__ */ createCentralIcon(
  IconMagnifyingGlass2,
  "Search",
);
export const Send = /* @__PURE__ */ createCentralIcon(
  IconPaperPlaneTopRight,
  "Send",
);
export const Settings2 = /* @__PURE__ */ createCentralIcon(
  IconSettingsGear2,
  "Settings2",
);
export const Share2 = /* @__PURE__ */ createCentralIcon(IconShareOs, "Share2");
export const ShieldCheck = /* @__PURE__ */ createCentralIcon(
  IconShieldCheck,
  "ShieldCheck",
);
export const SlidersHorizontal = /* @__PURE__ */ createCentralIcon(
  IconSettingsSliderHor,
  "SlidersHorizontal",
);
export const Sparkles = /* @__PURE__ */ createCentralIcon(
  IconSparklesThree,
  "Sparkles",
);
export const StepForward = /* @__PURE__ */ createCentralIcon(
  IconStepForwards,
  "StepForward",
);
export const Target = /* @__PURE__ */ createCentralIcon(IconTarget, "Target");
export const Trash2 = /* @__PURE__ */ createCentralIcon(
  IconTrashRounded,
  "Trash2",
);
export const TrendingDown = /* @__PURE__ */ createCentralIcon(
  IconTrending2,
  "TrendingDown",
);
export const TrendingUp = /* @__PURE__ */ createCentralIcon(
  IconTrending1,
  "TrendingUp",
);
export const Unlock = /* @__PURE__ */ createCentralIcon(IconUnlocked, "Unlock");
export const UsersRound = /* @__PURE__ */ createCentralIcon(
  IconPeople,
  "UsersRound",
);
export const Wallet = /* @__PURE__ */ createCentralIcon(IconWallet2, "Wallet");
export const WalletCards = /* @__PURE__ */ createCentralIcon(
  IconWallet5,
  "WalletCards",
);
export const WandSparkles = /* @__PURE__ */ createCentralIcon(
  IconMagicWand,
  "WandSparkles",
);
export const Waves = /* @__PURE__ */ createCentralIcon(
  IconLiveActivity,
  "Waves",
);
export const Wrench = /* @__PURE__ */ createCentralIcon(IconHammer, "Wrench");
export const X = /* @__PURE__ */ createCentralIcon(IconCrossSmall, "X");
export const XCircle = /* @__PURE__ */ createCentralIcon(
  IconCircleX,
  "XCircle",
);

export type CentralIconName = CentralIconComponent;

export type CentralIconProps = CentralIconComponentProps & {
  readonly icon: CentralIconName;
};

export function CentralIcon(props: CentralIconProps) {
  const { icon: IconComponent, ...iconProps } = props;
  return <IconComponent {...iconProps} />;
}

CentralIcon.displayName = "CentralIcon";
